import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { LogOut, Trash2, Phone, Mail, RefreshCw } from "lucide-react";
import WhatsAppIcon from "@/components/WhatsAppFloat";
import { waLinkForPhone } from "@/utils/whatsapp";
import Logo from "@/components/Logo";
import TestimonialManager from "@/pages/admin/TestimonialManager";
import JobManager from "@/pages/admin/JobManager";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STATUSES = [
  { key: "all", label: "All" },
  { key: "new", label: "New" },
  { key: "contacted", label: "Contacted" },
  { key: "closed", label: "Closed" },
];

const STATUS_STYLES = {
  new: "bg-primary/15 text-primary border-primary/30",
  contacted: "bg-sky-500/15 text-sky-400 border-sky-500/30",
  closed: "bg-zinc-700/40 text-zinc-400 border-zinc-600",
};

export default function AdminDashboard() {
  const [enquiries, setEnquiries] = useState(null);
  const [filter, setFilter] = useState("all");
  const [view, setView] = useState("enquiries");
  const navigate = useNavigate();

  const api = useMemo(() => {
    const instance = axios.create({ baseURL: API });
    instance.interceptors.request.use((config) => {
      const token = localStorage.getItem("hr99_token");
      if (token) config.headers.Authorization = `Bearer ${token}`;
      return config;
    });
    instance.interceptors.response.use(
      (r) => r,
      (err) => {
        if (err.response?.status === 401) {
          localStorage.removeItem("hr99_token");
          navigate("/admin/login");
        }
        return Promise.reject(err);
      }
    );
    return instance;
  }, [navigate]);

  const load = async () => {
    try {
      const { data } = await api.get("/enquiries");
      setEnquiries(data);
    } catch {
      setEnquiries([]);
    }
  };

  useEffect(() => {
    if (!localStorage.getItem("hr99_token")) {
      navigate("/admin/login");
      return;
    }
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const setStatus = async (id, status) => {
    try {
      const { data } = await api.patch(`/enquiries/${id}`, { status });
      setEnquiries((list) => list.map((e) => (e.id === id ? data : e)));
      toast.success(`Marked as ${status}`);
    } catch {
      toast.error("Could not update status");
    }
  };

  const remove = async (id) => {
    try {
      await api.delete(`/enquiries/${id}`);
      setEnquiries((list) => list.filter((e) => e.id !== id));
      toast.success("Enquiry deleted");
    } catch {
      toast.error("Could not delete enquiry");
    }
  };

  const logout = () => {
    localStorage.removeItem("hr99_token");
    navigate("/admin/login");
  };

  const filtered = (enquiries || []).filter((e) => filter === "all" || e.status === filter);
  const counts = STATUSES.reduce((acc, s) => {
    acc[s.key] = s.key === "all" ? (enquiries || []).length : (enquiries || []).filter((e) => e.status === s.key).length;
    return acc;
  }, {});

  return (
    <div className="min-h-screen bg-background text-foreground" data-testid="admin-dashboard">
      <header className="border-b border-white/10 bg-zinc-950/80 backdrop-blur-xl sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-6 md:px-12 h-[72px] flex items-center justify-between">
          <a href="/" className="flex items-center gap-4" data-testid="admin-logo">
            <Logo />
            <span className="text-xs uppercase tracking-[0.25em] text-zinc-500">Enquiries</span>
          </a>
          <div className="flex items-center gap-3">
            <button
              onClick={load}
              className="inline-flex items-center gap-2 border border-white/15 text-zinc-300 px-4 py-2 text-sm hover:border-primary hover:text-primary transition-colors"
              data-testid="admin-refresh-button"
            >
              <RefreshCw className="w-4 h-4" /> Refresh
            </button>
            <button
              onClick={logout}
              className="inline-flex items-center gap-2 border border-white/15 text-zinc-300 px-4 py-2 text-sm hover:border-red-400 hover:text-red-400 transition-colors"
              data-testid="admin-logout-button"
            >
              <LogOut className="w-4 h-4" /> Logout
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-6 md:px-12 py-10">
        <div className="inline-flex border border-white/10 mb-8" data-testid="admin-view-tabs">
          {[
            { k: "enquiries", l: "Enquiries" },
            { k: "testimonials", l: "Testimonials" },
            { k: "jobs", l: "Jobs" },
          ].map((t) => (
            <button
              key={t.k}
              onClick={() => setView(t.k)}
              className={`px-6 py-2.5 text-sm font-semibold transition-colors duration-300 ${
                view === t.k ? "bg-primary text-primary-foreground" : "text-zinc-400 hover:text-white"
              }`}
              data-testid={`admin-tab-${t.k}`}
            >
              {t.l}
            </button>
          ))}
        </div>
        {view === "testimonials" ? (
          <TestimonialManager />
        ) : view === "jobs" ? (
          <JobManager />
        ) : (
        <>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-px bg-white/10 border border-white/10 mb-10" data-testid="admin-stats">
          {STATUSES.map((s) => (
            <button
              key={s.key}
              onClick={() => setFilter(s.key)}
              className={`bg-zinc-950 p-6 text-left transition-colors hover:bg-zinc-900 ${filter === s.key ? "ring-1 ring-inset ring-primary" : ""}`}
              data-testid={`admin-filter-${s.key}`}
            >
              <p className="font-heading text-3xl md:text-4xl font-bold text-white">{counts[s.key]}</p>
              <p className="mt-1 text-xs uppercase tracking-[0.2em] text-zinc-500">{s.label}</p>
            </button>
          ))}
        </div>

        {enquiries === null ? (
          <p className="text-zinc-500" data-testid="admin-loading">Loading enquiries…</p>
        ) : filtered.length === 0 ? (
          <div className="border border-white/10 p-16 text-center" data-testid="admin-empty">
            <p className="font-heading text-2xl text-white">No enquiries here yet</p>
            <p className="text-zinc-500 mt-2 text-sm">New form submissions will appear in this list.</p>
          </div>
        ) : (
          <div className="space-y-4" data-testid="admin-enquiry-list">
            {filtered.map((e) => (
              <div key={e.id} className="bg-zinc-900 border border-zinc-800 p-6 md:p-8" data-testid={`admin-enquiry-${e.id}`}>
                <div className="flex flex-wrap items-start justify-between gap-4">
                  <div>
                    <div className="flex flex-wrap items-center gap-3">
                      <h3 className="font-heading text-xl font-medium text-white" data-testid={`enquiry-name-${e.id}`}>{e.name}</h3>
                      <span className={`text-xs px-3 py-1 border font-semibold uppercase tracking-wider ${STATUS_STYLES[e.status] || STATUS_STYLES.new}`} data-testid={`enquiry-status-${e.id}`}>
                        {e.status}
                      </span>
                      <span className="text-xs px-3 py-1 border border-white/10 text-zinc-400">{e.inquiry_type}</span>
                    </div>
                    <div className="mt-3 flex flex-wrap gap-x-6 gap-y-2 text-sm text-zinc-400">
                      <a href={`mailto:${e.email}`} className="inline-flex items-center gap-2 hover:text-primary transition-colors" data-testid={`enquiry-email-${e.id}`}>
                        <Mail className="w-3.5 h-3.5" /> {e.email}
                      </a>
                      <a href={`tel:${e.phone}`} className="inline-flex items-center gap-2 hover:text-primary transition-colors" data-testid={`enquiry-phone-${e.id}`}>
                        <Phone className="w-3.5 h-3.5" /> {e.phone}
                      </a>
                      <a
                        href={waLinkForPhone(e.phone, `Hi ${e.name}, this is HR99 Global following up on your enquiry.`)}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center gap-2 text-[#25D366] hover:brightness-110 transition"
                        data-testid={`enquiry-whatsapp-${e.id}`}
                      >
                        <WhatsAppIcon className="w-3.5 h-3.5" /> WhatsApp
                      </a>
                    </div>
                  </div>
                  <p className="text-xs text-zinc-600">{new Date(e.created_at).toLocaleString("en-IN", { dateStyle: "medium", timeStyle: "short" })}</p>
                </div>
                <p className="mt-4 text-zinc-300 text-sm leading-relaxed border-l-2 border-primary/40 pl-4" data-testid={`enquiry-message-${e.id}`}>
                  {e.message}
                </p>
                <div className="mt-6 flex flex-wrap gap-3">
                  {e.status !== "contacted" && (
                    <button onClick={() => setStatus(e.id, "contacted")} className="px-4 py-2 text-xs font-semibold border border-sky-500/40 text-sky-400 hover:bg-sky-500/10 transition-colors" data-testid={`enquiry-contacted-${e.id}`}>
                      Mark Contacted
                    </button>
                  )}
                  {e.status !== "closed" && (
                    <button onClick={() => setStatus(e.id, "closed")} className="px-4 py-2 text-xs font-semibold border border-white/15 text-zinc-300 hover:bg-white/5 transition-colors" data-testid={`enquiry-close-${e.id}`}>
                      Mark Closed
                    </button>
                  )}
                  {e.status !== "new" && (
                    <button onClick={() => setStatus(e.id, "new")} className="px-4 py-2 text-xs font-semibold border border-primary/40 text-primary hover:bg-primary/10 transition-colors" data-testid={`enquiry-reopen-${e.id}`}>
                      Reopen
                    </button>
                  )}
                  <button onClick={() => remove(e.id)} className="px-4 py-2 text-xs font-semibold border border-red-500/40 text-red-400 hover:bg-red-500/10 transition-colors inline-flex items-center gap-2" data-testid={`enquiry-delete-${e.id}`}>
                    <Trash2 className="w-3.5 h-3.5" /> Delete
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
        </>
        )}
      </main>
    </div>
  );
}
